/**
 * Section Loaders — server-side prop enrichment for CMS sections.
 *
 * Each entry receives CMS-resolved props + Request, returns enriched props.
 * Simple patterns (device, mobile) use framework mixins.
 * Complex logic (SearchResult, PDP fallback, Wishlist) is inline.
 */
import {
  registerSectionLoaders,
  withDevice,
  withMobile,
  withSearchParam,
  withSectionLoader,
  compose,
} from "@decocms/blocks/cms";
import { getVtexConfig } from "@decocms/apps-vtex";
import { blocks as generatedBlocks } from "../../.deco/blocks.gen";
import { isVivameProduct, pdpSlugFromPathname } from "~/lib/vivameBrand";

/**
 * Props de marca do header — tudo que muda entre Daniela Tombini e Vivame.
 * `searchbar`/`suggestions` ficam de fora de propósito: são referências a
 * loaders que o CMS já resolveu nos props da página; copiá-las do bloco
 * traria o `__resolveType` cru para o componente.
 */
const HEADER_BRAND_KEYS = [
  "alerts",
  "b2bButton",
  "brands",
  "cart",
  "darkLogo",
  "lightLogo",
  "logo",
  "navItems",
  "rulesBarText",
] as const;

function vivameHeaderProps(props: Record<string, unknown>): Record<string, unknown> {
  const vivame = generatedBlocks["Vivame Header"] as Record<string, unknown> | undefined;
  if (!vivame) return props;

  const branded: Record<string, unknown> = { ...props };
  for (const key of HEADER_BRAND_KEYS) {
    if (key in vivame) branded[key] = vivame[key];
  }
  // O bloco Vivame Header nasceu na landing `/vivame`, onde o header flutua
  // sobre o banner (logo branca). Numa PDP não há banner: manter o efeito
  // deixaria a logo branca invisível sobre fundo branco.
  branded.translucentEffect = false;
  return branded;
}

registerSectionLoaders({
  // Header da Vivame na PDP: as PLPs são cobertas por variante de seção no
  // CMS (pathname), mas `/:slug/p` não diz a marca na URL — só o catálogo
  // sabe. Ver src/lib/vivameBrand.ts.
  "site/sections/Header/Header.tsx": async (props: any, req) => {
    try {
      const slug = pdpSlugFromPathname(new URL(req.url).pathname);
      if (!slug) return props;
      return (await isVivameProduct(slug)) ? vivameHeaderProps(props) : props;
    } catch (err) {
      console.error("[Header] vivame brand loader error:", err);
      return props;
    }
  },

  "site/sections/Category/CategoryContent.tsx": withSectionLoader(() => import("~/sections/Category/CategoryContent")),
  // RelatedCategories NÃO tem loader de SSR de propósito: as buscas na VTEX
  // (árvore de categorias + imagem de cada irmã) custavam ~1,3s de TTFB no
  // pior caso (cache frio). O componente busca isso sozinho no client via
  // IntersectionObserver — ver src/sections/Category/RelatedCategories.tsx.
  "site/sections/LandingPages/CarouselSize.tsx": withSectionLoader(() => import("~/sections/LandingPages/CarouselSize")),
  "site/sections/LandingPages/HighlightsSection.tsx": withMobile(),
  // Enriquece cada aba com os produtos do cluster (busca por collectionId).
  // SEM esta linha o `export const loader` da seção nunca roda — o registry
  // de section loaders não é populado por convenção, só por registro
  // explícito — e todas as abas chegam ao componente sem `products`.
  "site/sections/LandingPages/TabCategories.tsx": withSectionLoader(() => import("~/sections/LandingPages/TabCategories")),
  "site/sections/LandingPages/TabCategoriesByCollection.tsx": withSectionLoader(() => import("~/sections/LandingPages/TabCategoriesByCollection")),
  "site/sections/LandingPages/WorkWithUs.tsx": withSectionLoader(() => import("~/sections/LandingPages/WorkWithUs")),
  "site/sections/Product/BuyTogether.tsx": withSectionLoader(() => import("~/sections/Product/BuyTogether")),
  // Busca os produtos da vitrine a partir do `collectionId` configurado.
  "site/sections/Product/SimpleShelf.tsx": withSectionLoader(() => import("~/sections/Product/SimpleShelf")),
  "site/sections/Product/SearchTitleAndResult.tsx": withSectionLoader(() => import("~/sections/Product/SearchTitleAndResult")),
  "site/sections/Seo/SeoPDP.tsx": withSectionLoader(() => import("~/sections/Seo/SeoPDP")),
  "site/sections/Seo/SeoPLP.tsx": withSectionLoader(() => import("~/sections/Seo/SeoPLP")),
  "site/sections/Seo/SeoBlogListing.tsx": withSectionLoader(() => import("~/sections/Seo/SeoBlogListing")),
  // Posts do blog: og:image/og:type e autor a partir do JSON-LD BlogPosting.
  "site/sections/Seo/SeoPage.tsx": withSectionLoader(() => import("~/sections/Seo/SeoPage")),
  "site/sections/Blog/BlogListing.tsx": withSectionLoader(() => import("~/sections/Blog/BlogListing")),
  "site/sections/Blog/BlogPost.tsx": withSectionLoader(() => import("~/sections/Blog/BlogPost")),

  // CategoryBanner: match URL against banner patterns
  "site/sections/Category/CategoryBanner.tsx": (props, req) => {
    const banners = (props as any).banners ?? [];
    const banner = banners.find(({ matcher }: { matcher: string }) => {
      try {
        return new URLPattern({ pathname: matcher }).test(req.url);
      } catch {
        return false;
      }
    });
    return { ...props, banner };
  },

  // Social
  "site/sections/Social/InstagramPosts.tsx": async (props: any, _req) => {
    const { facebookToken, layout, title, description } = props;
    if (!facebookToken) return props;
    try {
      const fields = "media_url,media_type,permalink";
      const apiUrl = `https://graph.instagram.com/me/media?access_token=${facebookToken}&fields=${fields}`;
      const { data } = (await fetch(apiUrl).then((r) => r.json())) as { data: any[] };
      return {
        data: (data || []).slice(0, layout?.numberOfPosts ?? 12),
        title,
        description,
        layout,
      };
    } catch (err) {
      console.error("[InstagramPosts] loader error:", err);
      return props;
    }
  },
});
